import { useEffect, useId, useRef, useState } from 'react'
import QRCode from 'qrcode'
import {
  ChatBubbleLeftRightIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import {
  MENTOR_PHONE,
  MENTOR_ZALO_URL,
} from '@/components/student/course-detail/types'

interface ConsultZaloModalProps {
  open: boolean
  onClose: () => void
}

const ConsultZaloModal = ({ open, onClose }: ConsultZaloModalProps) => {
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [copied, setCopied] = useState(false)
  const closeRef = useRef<HTMLButtonElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const generate = async () => {
      try {
        const dataUrl = await QRCode.toDataURL(MENTOR_ZALO_URL, {
          width: 220,
          margin: 2,
          color: { dark: '#000000', light: '#FFFFFF' },
        })
        if (!cancelled) setQrDataUrl(dataUrl)
      } catch {
        if (!cancelled) setQrDataUrl('')
      }
    }
    void generate()
    return () => {
      cancelled = true
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement as HTMLElement | null
    closeRef.current?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab') return

      const dialog = document.getElementById('consult-zalo-dialog')
      if (!dialog) return
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      previouslyFocused.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(MENTOR_PHONE)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* ignore */
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-eh-forest-ink/40 p-4 backdrop-blur-[2px]"
      role="presentation"
      onClick={onClose}
    >
      <div
        id="consult-zalo-dialog"
        className="relative w-full max-w-md border border-eh-border bg-eh-cream p-6 shadow-eh-soft"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-eh-sm text-eh-muted hover:bg-eh-keylime hover:text-eh-forest-ink"
          aria-label="Đóng"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <h2 id={titleId} className="eh-heading pr-10">
          Nhận tư vấn lộ trình qua Zalo
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-eh-muted">
          Chat với mentor qua Zalo để được tư vấn lộ trình phù hợp. Quét mã QR hoặc mở link
          bên dưới.
        </p>

        <div className="mt-6 flex flex-col items-center">
          <div className="rounded-eh border border-eh-border bg-white p-2.5 shadow-sm">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="QR Zalo tư vấn lộ trình" className="h-52 w-52" />
            ) : (
              <div className="flex h-52 w-52 items-center justify-center text-sm text-eh-muted">
                Đang tạo QR...
              </div>
            )}
          </div>

          <p className="mt-4 text-lg font-bold tracking-wide text-eh-forest-ink">
            {MENTOR_PHONE}
          </p>

          <div className="mt-4 flex w-full flex-wrap justify-center gap-2">
            <a
              href={MENTOR_ZALO_URL}
              target="_blank"
              rel="noreferrer"
              className="eh-btn min-w-[140px] flex-1 !min-h-[44px] !px-4 !py-2.5 text-sm"
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5" />
              Chat Zalo
            </a>
            <button
              type="button"
              onClick={copyPhone}
              className="eh-btn-secondary min-w-[140px] flex-1 !min-h-[44px] !px-4 !py-2.5 text-sm"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
              {copied ? 'Đã copy' : 'Copy SĐT'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConsultZaloModal
